// Third-parties
import React from 'react';
import classNames from 'classnames';
import { RowInfo, SortingRule } from 'react-table';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';

// Redux
import store from '@/redux/store';
import { selectIsLoadingParamsSome } from '@/redux/modules/ui/loaders';
import { ACCEPT_PORTFOLIO } from '@/redux/modules/contacts';

// Components
import DataTable from '../data/DataTable';
import { PillLabel, PortfolioLinePerformance } from '../misc';
import { OwnProps as PortfolioLinePerformanceProps } from '../misc/PortfolioLinePerformance';
import { MenuToggle, PrimaryButton, SecondaryButton, SecondaryLink } from '../buttons';
import ContactPastille from '../misc/ContactPastille';
import { Tooltip } from '../tooltips';
import CustomTypePills from '../flaggableInstruments/CustomTypePills';
import { LoadingSpinner } from '../common';

// Utils
import { FormattedMessage, formatLocalizedString, formatMessage } from '@/utils/i18n';
import { uniqueArray } from '@/utils/objects';
import { getPortfolioType } from '@/utils/portfolios';
import { portfolioToComparisonItem } from '@/utils/comparison';
import { isEnterpriseModel } from '@/utils/portfolio/utils';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../enums/Portfolio';
import ConnectedPrivateComponent from '../PrivateComponent';
import { PERMISSIONS } from '@/utils/permissions';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import FlaggableInstrument from '@/types/flaggable-instrument';
import LocalizedString from '@/types/localized-string';
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import PortfolioHolding from '@/types/portfolio-holding';
import Portfolio from '@/types/portfolio';

export enum WarningIcon {
  LOCK,
  ERROR,
}

export enum PortfolioAction {
  DELETE_SINGLE = 'deleteSingle',
  EDIT_SINGLE = 'editSingle',
  CLONE_SINGLE = 'cloneSingle',
  ACCEPT_SINGLE = 'acceptSingle',
}

export interface WarningProps {
  message: string;
  title: string;
  icon: WarningIcon;
}

type Props<T> = {
  portfolios?: T[];
  displayedColumns: PORTFOLIO_TABLE_COLUMNS[];
  displayedMetrics: PORTFOLIO_METRICS_FIELDS[];
  className?: string;
  warningDetails?: WarningProps;
  locked?: boolean;
  comparingPortfolioIds?: string[];
  showLoadMore?: boolean;
  onLoadMore?: () => void;
  onDelete?: (portfolios: T[]) => void;
  onEdit?: (portfolio: T) => void;
  onCompare?: (portfolios: T[]) => void;
  onAccept?: (portfolio: T) => void;
  onPortfoliosSelected?: (portfolios: T[]) => void;
  onUnselectPortfolios?: (portfolios: T[]) => void;
  // TODO: Make this prop mandatory once all table implement it
  onSort?: (sortingRules: SortingRule[]) => void;
  selectedTitle: string;
  messageIds?: {
    compareMultiple?: string;
    deleteMultiple?: string;
    deleteSingle?: string;
    editSingle?: string;
    acceptSingle?: string;
  };
  missingProductPredicate?: (portfolio: T) => boolean;
  canEditPredicate?: (portfolio: T) => boolean;
  canDeletePredicate?: (portfolio: T) => boolean;
  canClonePredicate?: (portfolio: T) => boolean;
  canAcceptPredicate?: (portfolio: T) => boolean;
  performanceSubcomponent?: (row: RowInfo) => React.ReactNode;
  forceSelectable?: boolean;
  user?: Nullable<User>;
  isThemeLight?: boolean;
};

type State<T> = {
  selectedPortfolios: T[];
  isComparing: boolean;
};

type MenuOption<T> = {
  key: PortfolioAction;
  value: string;
  onClick: ((portfolio: T) => void) | undefined;
  cyLabel: PortfolioAction;
  hidden: boolean | undefined;
  icon: string;
  className?: string;
  loaderKeys?: string | string[];
};

class PortfoliosTable<T extends { _id?: string; enterprise?: any; enterpriseGroups?: string[] } & FlaggableInstrument> extends React.Component<
  Props<T>,
  State<T>
> {
  canDeleteMultiple = true;

  readonly state: State<T> = {
    selectedPortfolios: [],
    isComparing: false,
  };

  isComparing = (portfolio: T) => {
    const { comparingPortfolioIds } = this.props;
    return !!comparingPortfolioIds && !!portfolio._id && comparingPortfolioIds.includes(portfolio._id);
  };

  onSelectPortfolios = (ids: string[]) => {
    const { forceSelectable, missingProductPredicate, portfolios } = this.props;
    const { selectedPortfolios } = this.state;

    const selected = portfolios
      ? portfolios.filter(
          portfolio => portfolio._id && ids.includes(portfolio._id) && (!missingProductPredicate || !missingProductPredicate(portfolio) || forceSelectable),
        )
      : [];

    const newSelection = uniqueArray(selectedPortfolios.concat(selected));

    this.setState({ selectedPortfolios: newSelection });
    if (this.props.onPortfoliosSelected) {
      this.props.onPortfoliosSelected(newSelection);
    }
  };

  getMenuOptions = (portfolio: T): MenuOption<T>[] => {
    const { messageIds } = this.props;
    return [
      {
        key: PortfolioAction.EDIT_SINGLE,
        value: formatMessage(messageIds?.editSingle || 'PortfoliosTable.Buttons.EditPortfolio'),
        onClick: this.props.onEdit,
        cyLabel: PortfolioAction.EDIT_SINGLE,
        hidden: this.props.canEditPredicate && !this.props.canEditPredicate(portfolio),
        icon: 'icon-ds-edit',
      },
      {
        key: PortfolioAction.DELETE_SINGLE,
        value: formatMessage(messageIds?.deleteSingle || 'PortfoliosTable.Buttons.DeletePortfolio'),
        className: 'destructive',
        onClick: (portfolio: T) => {
          if (this.props.onDelete) {
            return this.props.onDelete([portfolio]);
          }
        },
        cyLabel: PortfolioAction.DELETE_SINGLE,
        hidden: this.props.canDeletePredicate && !this.props.canDeletePredicate(portfolio),
        icon: 'icon-ds-delete',
      },
      {
        key: PortfolioAction.ACCEPT_SINGLE,
        value: formatMessage(messageIds?.acceptSingle || 'PortfoliosTable.Buttons.AcceptPortfolio'),
        onClick: (portfolio: T) => {
          if (this.props.onAccept) {
            return this.props.onAccept(portfolio);
          }
        },
        cyLabel: PortfolioAction.ACCEPT_SINGLE,
        hidden: this.props.canAcceptPredicate && !this.props.canAcceptPredicate(portfolio),
        icon: 'icon-save-to',
        loaderKeys: ACCEPT_PORTFOLIO.type,
      },
    ].filter(option => !option.hidden);
  };

  /**
   * Verifies if the menu options should be in a loading state.
   * This is inferred from the menu options definition using the "loaderKey" property.
   */
  shouldShowMenuLoader = (portfolio: T, menuOptions: MenuOption<T>[]) => {
    const keys = menuOptions.flatMap(option => option.loaderKeys ?? []);
    return selectIsLoadingParamsSome(store.getState(), keys).some(p => p?._id === portfolio._id);
  };

  getUnavailableActions = (portfolio: T) =>
    [
      {
        key: PortfolioAction.CLONE_SINGLE,
        value: 'Copied',
        hidden: this.props.canClonePredicate && !this.props.canClonePredicate(portfolio),
      },
      {
        key: PortfolioAction.EDIT_SINGLE,
        value: 'Edited',
        hidden: this.props.canEditPredicate && !this.props.canEditPredicate(portfolio),
      },
    ].filter(option => option.hidden);

  renderOptions = (portfolio: T): JSX.Element[] =>
    this.getMenuOptions(portfolio as any).map(item => (
      <Dropdown.Item key={item.key} role="menuitem" as="button" className={classNames(item.className)} onClick={() => item.onClick && item.onClick(portfolio)}>
        <i className={item.icon} />
        <span>{item.value}</span>
      </Dropdown.Item>
    ));

  unselectAll = () => {
    this.setState({ selectedPortfolios: [] });
  };

  onUnselectPortfolios = (ids: string[]) => {
    this.setState(prevState => ({
      selectedPortfolios: prevState.selectedPortfolios.filter(c => c._id && !ids.includes(c._id)),
    }));
    // Bubble up to the parent container to unselect the portfolios
    if (this.props.onUnselectPortfolios) {
      const portfoliosToUnselect = this.state.selectedPortfolios.filter(c => c._id && ids.includes(c._id));
      this.props.onUnselectPortfolios(portfoliosToUnselect);
    }
  };

  onStartComparison = () => {
    if (this.props.onCompare) {
      this.setState({ isComparing: true });
      this.props.onCompare(this.state.selectedPortfolios);
    }
  };

  deleteSelectedPortfolios = () => {
    const { onDelete } = this.props;
    if (onDelete) {
      return onDelete(this.state.selectedPortfolios);
    }
  };

  renderLock = (original: T) => {
    const unavailableActions = this.getUnavailableActions(original);
    let action = null;
    if (isEnterpriseModel(original)) {
      // Should be locked for enterprise models regardless of available actions because it should be edited from the admin section
      action = 'Edited';
    } else if (unavailableActions.length > 0) {
      action = unavailableActions.length === 2 ? 'EditedOrCopied' : unavailableActions[0].value;
    }
    return (
      action && (
        <Tooltip
          icon="icon-info"
          children={<i className="icon-lock-closed" />}
          title={formatMessage('PortfoliosTable.Warnings.Lock.Title')}
          message={formatMessage('PortfoliosTable.Warnings.Lock.Message', {
            action: formatMessage(`PortfoliosTable.Warnings.${action}`),
          })}
        />
      )
    );
  };

  getActions = () => {
    const { displayedColumns, messageIds, canDeletePredicate } = this.props;
    const { selectedPortfolios } = this.state;

    const canDelete = selectedPortfolios.every(portfolio => canDeletePredicate == null || canDeletePredicate(portfolio));

    const actions: JSX.Element[] = [];
    if (displayedColumns.includes(PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE) && this.canDeleteMultiple && canDelete) {
      actions.push(
        <PrimaryButton key="action-delete" compact icon="ds-delete" onClick={this.deleteSelectedPortfolios}>
          <FormattedMessage
            id={messageIds?.deleteMultiple || 'PortfoliosTable.Buttons.DeleteSelectedPortfolios'}
            values={{ count: this.state.selectedPortfolios.length }}
          />
        </PrimaryButton>,
      );
    }

    if (displayedColumns.includes(PORTFOLIO_TABLE_COLUMNS.COMPARE)) {
      actions.push(
        <PrimaryButton key="action-compare" loading={this.state.isComparing} compact icon="plus" onClick={this.onStartComparison}>
          <FormattedMessage id={messageIds?.compareMultiple || 'PortfoliosTable.Buttons.AddToComparison'} />
        </PrimaryButton>,
      );
    }

    return actions;
  };

  showSelectedTitle = () => {
    if (this.props.selectedTitle) {
      return formatMessage(this.props.selectedTitle, { count: this.state.selectedPortfolios.length });
    }
    return '';
  };

  renderNameCell(original: T, value: LocalizedString | string) {
    return (
      <>
        <Tooltip message={typeof value === 'string' ? value : formatLocalizedString(value)} data-private data-dd-privacy="mask">
          <span className="portfolio-name">{typeof value === 'string' ? value : formatLocalizedString(value)}</span>
        </Tooltip>
        <CustomTypePills className="custom-type-pill" flaggableInstrument={original} />
      </>
    );
  }

  renderGroupCell(value: string[], user: Nullable<User> | undefined) {
    if (!value || value.length === 0) {
      return (
        <Tooltip message={formatMessage('PortfoliosTable.Data.MyModel')} data-private data-dd-privacy="mask">
          <span>{formatMessage('PortfoliosTable.Data.MyModel')}</span>
        </Tooltip>
      );
    }

    const enterpriseModelName = user?.enterprise?.name
      ? formatMessage('PortfoliosTable.Data.EnterpriseModelName', { name: user.enterprise.name })
      : formatMessage('PortfoliosTable.Data.EnterpriseModel');

    return (
      <Tooltip message={enterpriseModelName} data-private data-dd-privacy="mask">
        <span>{enterpriseModelName}</span>
      </Tooltip>
    );
  }

  getTableColumns = () => {
    const { displayedColumns, locked, user, onCompare, missingProductPredicate, isThemeLight } = this.props;
    const { selectedPortfolios } = this.state;
    const headerClassName = isThemeLight ? 'sortable' : '';

    const columns = [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
        Header: () => <span className={headerClassName}>{formatMessage('PortfoliosTable.Headers.Name')}</span>,
        accessor: 'name',
        Cell: ({ original, value }: { value: LocalizedString; original: T }) => this.renderNameCell(original, value),
        sortable: true,
        sortMethod: (a: LocalizedString | string, b: LocalizedString | string) =>
          (typeof a === 'string' ? a : formatLocalizedString(a)) > (typeof b === 'string' ? b : formatLocalizedString(b)) ? 1 : -1,
        minWidth: 120,
      },
      {
        key: 'MissingProduct',
        Cell: ({ original }: { original: T }) =>
          missingProductPredicate && missingProductPredicate(original) ? (
            <Tooltip
              icon="icon-info"
              children={<i className="icon-error" />}
              title={formatMessage('PortfoliosTable.Warnings.MissingProduct.Title')}
              message={formatMessage('PortfoliosTable.Warnings.MissingProduct.Message')}
            />
          ) : null,
        width: 28,
        sortable: false,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
        Cell: ({ original }: { original: T }) => this.renderLock(original),
        width: 28,
        sortable: false,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.GROUP,
        Header: () => <span className={headerClassName}>{formatMessage('PortfoliosTable.Headers.Group')}</span>,
        Cell: ({ value }: { value: string[] }) => this.renderGroupCell(value, user),
        mawWidth: 80,
        accessor: 'enterpriseGroups',
        sortable: !!isThemeLight,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
        Header: () => <span className={headerClassName}>{formatMessage('PortfoliosTable.Headers.RiskProfile')}</span>,
        accessor: 'riskProfile',
        Cell: ({ value }: { value: ContactRiskProfile | undefined }) =>
          value !== undefined ? (
            <Tooltip message={formatLocalizedString(value.profile.title)}>
              <span>
                <PillLabel text={formatLocalizedString(value.profile.title)} />
              </span>
            </Tooltip>
          ) : null,
        sortable: !!isThemeLight,
        maxWidth: 120,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.OWNERS,
        Header: () => <span>{formatMessage('PortfoliosTable.Headers.Owners')}</span>,
        accessor: 'contact',
        Cell: ({ value }: { value: GenericContact }) =>
          // This will need to be refactored when portfolios are able to include > 1 contact
          value ? (
            <div className="d-flex flex-row">
              <div className="owner-image-spacing">
                <ContactPastille contact={value} />
              </div>
              {/* <div className="owner-image-spacing">
                <ContactPastille contact={value} />
              </div>
              <div className="owner-image-spacing ms-1">
                <span className="grey-11 more-owners">+1 more</span>
              </div> */}
            </div>
          ) : null,
        minWidth: 100,
        sortable: false,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
        Header: () => <span>{formatMessage('PortfoliosTable.Headers.LastModified')}</span>,
        accessor: 'dates.savedAt',
        Cell: ({ value }: { value: Date }) =>
          value ? (
            <Tooltip message={moment(value).format(formatMessage('PortfoliosTable.Data.LastModifiedDateFormat'))}>
              <span className="grey-11">{moment(value).format(formatMessage('PortfoliosTable.Data.LastModifiedDateFormat'))}</span>
            </Tooltip>
          ) : null,
        maxWidth: 180,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.HOLDINGS,
        Header: () => <span>{formatMessage('PortfoliosTable.Headers.Holdings')}</span>,
        accessor: 'holdings',
        Cell: ({ value }: { value: PortfolioHolding[] }) => (value ? <span className="grey-11"> {value.length} </span> : null),
        sortable: false,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        expander: true,
        disabled: true,
        sortable: false,
        Expander: ({ isExpanded, original }: { isExpanded: boolean; original: T }) =>
          missingProductPredicate && missingProductPredicate(original) ? null : (
            <ConnectedPrivateComponent permissions={[PERMISSIONS.SHOW_PORTFOLIO_DETAILS]}>
              {() => (
                <SecondaryLink className="details-toggle">
                  {formatMessage('PortfoliosTable.Buttons.Details')} {isExpanded ? <i>&times;</i> : <i className="icon-cheveron-down" />}
                </SecondaryLink>
              )}
            </ConnectedPrivateComponent>
          ),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
        Cell: ({ original }: { original: T }) =>
          onCompare ? (
            <SecondaryButton
              className="ms-auto"
              loading={this.isComparing(original)}
              onClick={() => onCompare([original])}
              compact
              disabled={selectedPortfolios.length > 0}
            >
              <FormattedMessage id={`PortfolioCard.${this.isComparing(original) ? 'Comparing' : 'Compare'}`} />
            </SecondaryButton>
          ) : null,
        width: 150,
        sortable: false,
        show: !!this.props.onCompare,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
        Cell: ({ original }: { original: T }) => {
          const menuOptions = this.getMenuOptions(original);

          if (!menuOptions.find(option => option.key === PortfolioAction.DELETE_SINGLE)) {
            this.canDeleteMultiple = false;
          }

          if (this.shouldShowMenuLoader(original, menuOptions)) {
            return <LoadingSpinner />;
          }

          if (menuOptions.length > 0) {
            return <MenuToggle options={this.renderOptions(original)} alignRight disabled={selectedPortfolios.length > 0} />;
          }

          return null;
        },
        sortable: false,
        width: 48,
      },
    ];

    return columns.filter(
      column =>
        // Only use columns passed as props as well as the Warning if warningDetails is defined.
        displayedColumns.includes(column.key as PORTFOLIO_TABLE_COLUMNS) || (locked && column.key === 'Locked') || column.key === 'MissingProduct',
    );
  };

  getSubComponentFunction = () => {
    // If no column is an expander, don't return any subcomponent.
    // Prevents rendering the default react-table expand arrow.
    if (!this.getTableColumns().some(col => col.expander)) {
      return undefined;
    }

    return (row: RowInfo) => {
      // If a subComponent was passed as prop render it
      if (this.props.performanceSubcomponent) {
        return this.props.performanceSubcomponent(row);
      }

      // Otherwise default to the PortfolioLinePerformance that fetches data for a regular portfolio
      const subComponentProps: PortfolioLinePerformanceProps<Portfolio> = {
        portfolio: row.original,
        transformPortfolioToComparisonItem: (appRegion, validateIncomeStream) => portfolioToComparisonItem(row.original, appRegion, validateIncomeStream),
        displayedMetrics: this.props.displayedMetrics,
        endpointEntity: 'portfolios',
        queryParams: {
          contactId: row.original.contact?._id,
          portfolioType: getPortfolioType(row.original),
        },
      };
      return <PortfolioLinePerformance {...subComponentProps} />;
    };
  };

  render() {
    const { className, forceSelectable, missingProductPredicate, portfolios, showLoadMore } = this.props;

    return (
      <div className={classNames('portfolios-table d-flex flex-column', className)}>
        <DataTable
          sortable
          serverSideSorting
          canSelect
          collapseOnDataChange={false}
          showLoadMore={showLoadMore}
          scrollAncestor=".app-scrollbar-container"
          columns={this.getTableColumns()}
          data={portfolios || null}
          subComponent={this.getSubComponentFunction()}
          onLoadMore={this.props.onLoadMore}
          onSelectItems={this.onSelectPortfolios}
          onUnselectItems={this.onUnselectPortfolios}
          onSortingChange={this.props.onSort}
          selected={this.state.selectedPortfolios}
          selectedActions={this.getActions()}
          selectedTitle={this.showSelectedTitle()}
          isUnselectable={row => {
            if (missingProductPredicate && !forceSelectable) {
              return missingProductPredicate(row.original);
            }

            return false;
          }}
          expanderDefaults={{ width: 90 }}
        />
      </div>
    );
  }
}

export default PortfoliosTable;
